/**
 * Builds a preview of the final prompt sent to the LLM.
 * This mirrors the prompt construction in functions/api/analyze-video.js
 * 
 * @param {number} frameCount - Number of frames being sent
 * @param {string|null} poseAnalysis - Optional pose analysis data
 * @param {string|null} customPrompt - Optional custom user instructions
 * @param {string|null} contextInfo - Optional context priming info
 * @returns {string} - The full prompt text (without actual images)
 */
export function buildPromptPreview(frameCount, poseAnalysis = null, customPrompt = null, contextInfo = null) {
  // Start with custom user instructions if provided
  let userPrompt = customPrompt ? `${customPrompt}\n\n` : ''
  
  // Add context info if provided from context priming
  if (contextInfo) {
    userPrompt += `--- SCENE CONTEXT ---
${contextInfo}
--- END SCENE CONTEXT ---

`
  }
  
  userPrompt += `
Provide expert basketball coaching feedback based on these ${frameCount} frames extracted from a single video in chronological order.
Each frame is annotated in the top-left corner with its frame number and timestamp (e.g., "Frame 1 | 2.50s").
${poseAnalysis ? 'The frames include skeletal pose overlay showing body joint positions.' : ''}
${contextInfo ? 'Use the scene context provided above to tailor your feedback appropriately (e.g., adjust coaching style for the player\'s age/skill level).' : ''}

Analyze the progression of technique and performance across the sequence.
Don't mention the frame numbers or timestamps in your analysis, but consider this as a consecutive sequence of frames based on the timestamps.

Return JSON only:
{
  "overallScore": number (0-100),
  "analysis": "basketball coaching assessment of technique and performance",
  "insights": [
    { "icon": "<emoji>", "title": "<title>", "description": "<detail>" }
  ],
  "suggestions": [
    "actionable suggestion 1",
    "actionable suggestion 2"
  ]
}`

  // Append pose analysis data if provided
  if (poseAnalysis) {
    userPrompt += `

--- ADDITIONAL POSE ANALYSIS DATA ---
The following joint angle measurements were extracted using MediaPipe pose detection.
Use this data to provide more precise coaching feedback on body positioning and movement mechanics.

${poseAnalysis}
--- END POSE DATA ---`
  }

  // Add image placeholders to show what's being sent
  let fullPrompt = `=== TEXT CONTENT ===
${userPrompt}

=== IMAGES (${frameCount} frames) ===
`

  for (let i = 1; i <= frameCount; i++) {
    fullPrompt += `[Frame ${i} - JPEG image, high detail]\n`
  }

  fullPrompt += `
=== MODEL ===
gpt-5.1 (via Cloudflare AI Gateway)
max_completion_tokens: 4000`

  return fullPrompt
}

